'use client';
// `window` exists in browser tabs; Web Worker scopes (including extension
// service workers) expose `importScripts` on `globalThis`. If neither is
// present we are in Node.js and this module should not have been imported.
if (typeof window === 'undefined' &&
    !('importScripts' in globalThis) &&
    !globalThis.process?.env?.VITEST) {
    throw new Error('pplx/frontend/logger/browser must only be imported in browser environments. ' +
        'Use pplx/frontend/logger/node for server-side code.');
}
import { redactUrlQueryData } from '#pplx/frontend/common/redactAuthParams.js';
import { isSupportedBrowser } from '#pplx/frontend/common/supportedBrowsers.js';
import { formatLog } from '#pplx/frontend/logger/internal/core.js';
import { redactSignedUrlText } from '#pplx/frontend/logger/redactSignedUrl.js';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { match } from 'ts-pattern';
/**
 * Removes query strings and fragments from URLs sent through Datadog Logs.
 * Paths remain available for debugging. This does not rely on URL parsing, so
 * malformed URL-like input cannot bypass it.
 */
export const redactLogUrl = redactUrlQueryData;
function getUseServiceWorker() {
    return (typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        !!navigator.serviceWorker.controller);
}
/**
 * Known browser extension error message patterns — not actionable.
 * Extensions inject code into pages and throw errors our instrumentation captures.
 */
const EXTENSION_ERROR_PATTERNS = [
    'runtime.sendMessage', // Chrome/browser extension runtime API (e.g. "Invalid call to runtime.sendMessage()")
    'runtime.connect', // Extension port connections
    'Extension context invalidated', // Extension updated/disabled mid-session
    'Receiving end does not exist', // Extension messaging failure
    'react-render-tracker', // React Render Tracker extension
    'A listener indicated an asynchronous response', // Extension message listener
    '_AutofillCallbackHandler', // Instagram/Facebook in-app WebView autofill callback
];
const EXTENSION_PROTOCOL_RE = /(?:chrome|moz|safari(?:-web)?)-extension:\/\//;
function isExtensionError(message, stack) {
    if (message && EXTENSION_ERROR_PATTERNS.some((p) => message.includes(p))) {
        return true;
    }
    return !!stack && EXTENSION_PROTOCOL_RE.test(stack);
}
/**
 * Log for browser, which sends directly to DD
 * @param severity severity
 * @param formattedLog formatted log
 */
function clientLog(severity, { message, error, extra }) {
    const logParams = [message, extra, error];
    match(severity)
        .with('error', () => {
        datadogLogs.logger.error(...logParams);
        try {
            datadogRum.addError(error, { message, extra });
        }
        catch {
            // Ignore since on extension there's no RUM
        }
    })
        .with('warn', () => datadogLogs.logger.warn(...logParams))
        .with('log', () => datadogLogs.logger.info(...logParams))
        .with('debug', () => datadogLogs.logger.debug(...logParams))
        .with('info', () => datadogLogs.logger.info(...logParams))
        .exhaustive();
}
/**
 * So, why do we need this? Because w/o proper formatting, logs look super messed up in Datadog
 * for web-nextjs. We're also adding logs to the RUM error tracking.
 */
export const logger = {
    log: (...messages) => {
        clientLog('debug', formatLog(...messages));
    },
    error: (...messages) => {
        clientLog('error', formatLog(...messages));
    },
    warn: (...messages) => {
        clientLog('warn', formatLog(...messages));
    },
    debug: (...messages) => {
        clientLog('debug', formatLog(...messages));
    },
    info: (...messages) => {
        clientLog('info', formatLog(...messages));
    },
};
export function overrideGlobalConsole() {
    // This is no-op in the browser because DD already overrides console
}
/**
 * Initialize the logger with global configuration
 * @param options options
 */
export function initialize({ clientToken, version, env, debug, debugLogLevel = 'error', sessionSampleRate, service = 'web-nextjs', beforeSend, }) {
    const browserSupported = isSupportedBrowser(navigator.userAgent);
    datadogLogs.init({
        clientToken,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service,
        env,
        version,
        sessionSampleRate,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['warn', 'error', 'info'],
        silentMultipleInit: debug,
        beforeSend: (event, context) => {
            if (!browserSupported) {
                return false;
            }
            event.view = event.view ?? {};
            // Query strings can contain user-authored values on any route. Remove
            // them at the Logs boundary without changing RUM or analytics.
            event.view.url = redactLogUrl(event.view?.url ?? '');
            if (event.view.referrer) {
                event.view.referrer = redactLogUrl(event.view.referrer);
            }
            // Presigned URLs are replayable download credentials; scrub their params.
            if (event.http?.url) {
                const rawHttpUrl = event.http.url;
                // Keep the signed-URL scrubber as defense in depth if URL parsing or
                // this broader policy changes later.
                const redactedHttpUrl = redactSignedUrlText(redactLogUrl(rawHttpUrl));
                event.http.url = redactedHttpUrl;
                // Datadog network errors duplicate the exact request URL in `message`.
                if (typeof event.message === 'string') {
                    event.message = event.message.split(rawHttpUrl).join(redactedHttpUrl);
                }
            }
            // Ignore reddit tracking pixel errors
            if (event.http?.url.includes('reddit.com')) {
                return false;
            }
            // 0 status_code means browser blocked the request, drop this to warn
            if (event.http?.status_code === 0) {
                event.status = 'warn';
            }
            // This is 3PD script error that is super inactionable, drop it
            if (event.error?.handling === 'unhandled' &&
                // The type says that `message` is supposed to be a string, but it's sometimes an `object`
                event.message?.includes?.('Script error.') &&
                !event.error?.cause) {
                return false;
            }
            // Safari throws "Importing a module script failed" when loading stale cached
            // JS that references chunks removed by a new deployment. Already handled by
            // Dynamic import retry + critical reload; downgrade to warn. The type says
            // that `message` is supposed to be a string, but it's sometimes an `object`
            if (event.message?.includes?.('Importing a module script failed')) {
                event.status = 'warn';
            }
            // External SDK/service-worker heartbeat timeout — not actionable. The type
            // says that `message` is supposed to be a string, but it's sometimes an `object`
            if (event.message?.includes?.('Provider unavailable')) {
                return false;
            }
            // Browser extension errors — not actionable
            if (isExtensionError(typeof event.message === 'string' ? event.message : undefined, event.error?.stack)) {
                return false;
            }
            const shouldSend = event.status === 'error' || event.status === 'warn' || event.status === 'info';
            if (!shouldSend) {
                return false;
            }
            return beforeSend?.(event, context) ?? true;
        },
    });
    setGlobalContext({ useServiceWorker: getUseServiceWorker() });
    if (debug) {
        // @ts-expect-error private api
        datadogLogs._setDebug(true);
        datadogLogs.onReady(() => {
            datadogLogs.logger.setHandler(['http', 'console']);
            datadogLogs.logger.setLevel(debugLogLevel);
        });
    }
}
/**
 * Set global context for the logger
 * @param context context
 */
export function setGlobalContext(context) {
    Object.entries(context).forEach(([key, value]) => {
        datadogLogs.setGlobalContextProperty(key, value);
    });
}
//# sourceMappingURL=browser.js.map